/**
 * Market Forecasts Page
 * 
 * Standalone page for managing IHS/S&P Mobility market forecast uploads and reconciliation.
 * This is the primary interface for vehicle program forecast updates.
 * 
 * Features:
 * - Upload new IHS data files
 * - Review changes (new opportunities, removed programs, updates)
 * - Apply changes with full audit trail
 * - View reconciliation history
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Database, Crosshair, ArrowRight, List, RefreshCw } from 'lucide-react';
import IHSReconciliationPage from '../components/competitive-intelligence/reconciliation/IHSReconciliationPage';
import IHSForecastCatalog from '../components/ihs/IHSForecastCatalog';

const IHSDataManagement = () => {
  const [activeView, setActiveView] = useState('catalog');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Database className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900">Market Forecasts</h1>
                <p className="text-sm text-slate-500">
                  Review IHS/S&amp;P Mobility vehicle program forecasts and apply market-volume changes
                </p>
              </div>
            </div>
            
            {/* Quick Link to Competitive Intelligence */}
            <Link 
              to="/competitive-intelligence"
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            >
              <Crosshair className="h-4 w-4" />
              <span>Competitive Intelligence</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('catalog')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeView === 'catalog'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <List className="w-4 h-4 inline mr-1.5" />
              All Forecasts
            </button>
            <button
              onClick={() => setActiveView('review')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeView === 'review'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <RefreshCw className="w-4 h-4 inline mr-1.5" />
              Review Upload Changes
            </button>
          </div>
        </div>

        {activeView === 'catalog' ? (
          <IHSForecastCatalog
            mode="inline"
            title="All Forecasts"
            subtitle="Accepted IHS/S&P Mobility vehicle carlines available for pipeline creation, Program 360, and S&OP planning."
          />
        ) : (
          <IHSReconciliationPage />
        )}
      </div>
    </div>
  );
};

export default IHSDataManagement;
